import type { ReactNode } from 'react';

/**
 * Icon actions.
 *
 * Compact actions in a list header or a panel head are icons: New, Refresh,
 * Delete, Edit. They repeat on every screen, a dispatcher learns them once, and
 * the words were eating room a narrow sidebar does not have.
 *
 * Every one carries both a `title` and an `aria-label` with the same words. The
 * tooltip serves the mouse and the label serves a screen reader; an icon button
 * with neither is a button that only its author can use.
 *
 * Anything consequential keeps its words. "Save changes", "Create order",
 * "Import" and the confirm-sheet buttons stay written out, because a
 * single-glyph commit is how somebody presses the wrong thing.
 */

export function IconButton({
  label,
  onClick,
  children,
  tone,
  disabled,
  pressed,
  small,
}: {
  /** Used for both the tooltip and the accessible name. */
  label: string;
  onClick: () => void;
  children: ReactNode;
  tone?: 'danger' | 'primary';
  disabled?: boolean;
  pressed?: boolean;
  small?: boolean;
}) {
  const classes = ['btn', 'btn-icon'];
  if (small) classes.push('btn-small');
  if (tone === 'danger') classes.push('btn-danger');
  if (tone === 'primary') classes.push('btn-primary');

  return (
    <button
      type="button"
      className={classes.join(' ')}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
    >
      {children}
    </button>
  );
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M7 2.5v9M2.5 7h9" {...stroke} />
    </svg>
  );
}

/** A message to a driver's phone. */
export function BellIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M3.5 9.5V6.5a3.5 3.5 0 0 1 7 0v3l1 1.2h-9z" {...stroke} />
      <path d="M5.8 12a1.3 1.3 0 0 0 2.4 0" {...stroke} />
    </svg>
  );
}

export function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M12 7a5 5 0 11-1.6-3.7" {...stroke} />
      <path d="M12 1.6V4.2H9.4" {...stroke} />
    </svg>
  );
}

export function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2.6 3.6h8.8M5.4 3.6V2.4h3.2v1.2M3.8 3.6l.5 8h5.4l.5-8" {...stroke} />
    </svg>
  );
}

/*
 * Taking a stop off a route: a pin with a line through it. Not a bin -- the
 * order stays, and the two actions sit next to each other, so they must not
 * read alike at a glance.
 */
export function UnassignIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M7 1.9a3.3 3.3 0 013.3 3.3c0 2.5-3.3 6.9-3.3 6.9S3.7 7.7 3.7 5.2A3.3 3.3 0 017 1.9z" {...stroke} />
      <path d="M2 12L12 2" {...stroke} />
    </svg>
  );
}

export function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M9.6 1.9l2.5 2.5-7 7L2.2 12l.6-2.9 6.8-7.2z" {...stroke} />
    </svg>
  );
}

export function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="4.6" y="4.6" width="7.2" height="7.2" rx="1.3" {...stroke} />
      <path d="M9.4 2.2H2.2v7.2" {...stroke} />
    </svg>
  );
}

/*
 * Copying inward: the same two sheets as CopyIcon with the arrow reversed, so
 * the pair reads as a direction rather than as two unrelated actions.
 */
export function CopyIntoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="4.6" y="4.6" width="7.2" height="7.2" rx="1.3" {...stroke} />
      <path d="M9.4 2.2H2.2v7.2" {...stroke} />
      <path d="M6.3 8.1h3.4M8.2 6.6l1.5 1.5-1.5 1.5" {...stroke} />
    </svg>
  );
}

export function CalendarPlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="1.8" y="2.8" width="10.4" height="9.4" rx="1.4" {...stroke} />
      <path d="M1.8 5.8h10.4M4.4 1.6v2.2M9.6 1.6v2.2M7 7.4v3M5.5 8.9h3" {...stroke} />
    </svg>
  );
}

export function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M1 7s2.2-3.6 6-3.6S13 7 13 7s-2.2 3.6-6 3.6S1 7 1 7z" {...stroke} />
      <circle cx="7" cy="7" r="1.6" {...stroke} />
    </svg>
  );
}
